import { FilterList } from '@mui/icons-material';
import { Button, Skeleton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { visitApi } from 'src/api/visit';
import { LOCAL_STORAGE_KEYS } from 'src/constants/localStorage';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import VisitHistoryFilters, {
  VisitFilters,
} from 'src/sections/@dashboard/visit/components/VisitHistoryFilters';
import OrganizationProfessionalVisitHistoryTableSkeleton from './OrganizationProfessionalVisitHistoryTableSkeleton';
import OrganizationProfessionalVisitsHistoryTable from './OrganizationProfessionalVisitsHistoryTable';
import { translateKey } from 'src/locales/translate';


export const OrganizationProfessionalVisitHistoryContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { professionalId } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = localStorage.getItem(
      LOCAL_STORAGE_KEYS.ORG_PROFESSIONAL_VISIT_HISTORY_ROWS_PER_PAGE,
    );
    return stored ? parseInt(stored, 10) : 25;
  });
  const [filters, setFilters] = useState<VisitFilters>(() => {
    const stored = localStorage.getItem(
      LOCAL_STORAGE_KEYS.ORG_PROFESSIONAL_VISIT_HISTORY_FILTERS,
    );
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        startDate: parsed.startDate ? new Date(parsed.startDate) : null,
        endDate: parsed.endDate ? new Date(parsed.endDate) : null,
        // Absent from anything stored before the Zamknięto filter existed,
        // which reads as "not filtered by closure" — the right default.
        closedFrom: parsed.closedFrom ? new Date(parsed.closedFrom) : null,
        closedTo: parsed.closedTo ? new Date(parsed.closedTo) : null,
      };
    }
    return {};
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    selectedRole,
    selectedOrganization,
    isOrganizationAdminRoleSelected,
  } = useOrganizationContext();
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    [
      'professional-organization-visits-by-professional-id',
      selectedOrganization?.id,
      selectedRole,
      professionalId,
      page,
      rowsPerPage,
      filters.userName,
      filters.profileName,
      filters.startDate,
      filters.endDate,
      filters.closedFrom,
      filters.closedTo,
      filters.noteStatuses,
    ],
    () =>
      visitApi.visitControllerGetOrganizationProfessionalVisitHistoryByProfessionalIdParam(
        {
          professionalId: professionalId || '',
          page,
          limit: rowsPerPage,
          userName: filters.userName,
          profileName: filters.profileName,
          startDate: filters.startDate?.toISOString(),
          endDate: filters.endDate?.toISOString(),
          closedFrom: filters.closedFrom
            ? format(filters.closedFrom, 'yyyy-MM-dd')
            : undefined,
          closedTo: filters.closedTo
            ? format(filters.closedTo, 'yyyy-MM-dd')
            : undefined,
          noteStatuses: filters.noteStatuses?.join(',') || undefined,
        },
      ),
    {
      retry: 2,
      enabled: !!selectedOrganization?.id && isOrganizationAdminRoleSelected,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    if (!data && !isLoading && isError) {
      enqueueSnackbar(translateKey("snackbar.historyFetchError"), {
        variant: 'error',
      });
    }
  }, [data, isLoading, isError, enqueueSnackbar]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(newRowsPerPage);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.ORG_PROFESSIONAL_VISIT_HISTORY_ROWS_PER_PAGE,
      newRowsPerPage.toString(),
    );
  };

  const handleApplyFilters = (newFilters: VisitFilters) => {
    setFilters(newFilters);
    setPage(0);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.ORG_PROFESSIONAL_VISIT_HISTORY_FILTERS,
      JSON.stringify(newFilters),
    );
  };

  const handleRemoveFilter = (filterKey: keyof VisitFilters) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setPage(0);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.ORG_PROFESSIONAL_VISIT_HISTORY_FILTERS,
      JSON.stringify(newFilters),
    );
  };

  return !isLoading ? (
    <>
      <Stack
        p={3}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6">{translateKey("search.book.promoHistoryTitle")}</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setFiltersOpen(!filtersOpen)}
          color={filtersOpen ? 'primary' : 'inherit'}
          data-testid="visit-history-filters-toggle"
        >
          {translateKey('visit.historyFiltersTooltip')}
        </Button>
      </Stack>
      <VisitHistoryFilters
        onApplyFilters={handleApplyFilters}
        onRemoveFilter={handleRemoveFilter}
        initialFilters={filters}
        isOpen={filtersOpen}
      />
      <OrganizationProfessionalVisitsHistoryTable
        data={data?.data?.data || []}
        total={data?.data?.total || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        isFetching={isFetching}
        refetch={() => refetch()}
      />
    </>
  ) : (
    <Stack sx={{ minHeight: '560px', height: 1 }}>
      <Stack p={3}>
        <Skeleton variant="rounded" height="28px" width="260px" />
      </Stack>
      <OrganizationProfessionalVisitHistoryTableSkeleton />
    </Stack>
  );
};
